import { computed, observable } from 'mobx';
import { CXBaseBlock } from '~/components/ClientExperience/OneFlow/models/cx-base-block.model';
import { QuestionType, TQuestion } from '~/types/builder.types';
import { IQuestionBlockResponse } from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';
import { variableValueToAttachments } from '~/common/UploadQuestion/upload-question.utils';

export class CXQuestionBlock extends CXBaseBlock {
    type: 'question_block';
    @observable question: TQuestion;
    @observable connected_variable_id: string;

    constructor(
        block: IQuestionBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.question = block.question;
    }

    @computed
    get isPendingClientCompletion() {
        return this.question.is_required && !this.connectedVariable.value;
    }

    @computed
    get variableRawValue() {
        const rawValue =
            this.rootStore.flowStore.variables[this.connected_variable_id]
                ?.raw_value;

        return this.question.question_type === QuestionType.Upload
            ? variableValueToAttachments(rawValue)
            : rawValue;
    }

    @computed
    get optionsById() {
        if (!('options' in this.question)) {
            return null;
        }

        const { options } = this.question;
        return options.reduce((hash, option) => {
            hash[option.option_id] = option;

            return hash;
        }, {});
    }

    getFieldValue(selectedValue) {
        if (!this.question || !selectedValue) {
            return selectedValue;
        }

        if (
            'options' in this.question &&
            (this.question.question_type === QuestionType.Dropdown ||
                this.question.question_type === QuestionType.SingleChoice)
        ) {
            return this.question.options.find(
                x => x.option_id === selectedValue
            )?.answer_text;
        }

        if (this.question.question_type === QuestionType.MultiChoice) {
            return selectedValue
                .map(optionId => this.optionsById[optionId]?.answer_text)
                .filter(x => x)
                .join(', ');
        }

        return selectedValue;
    }
}
